import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Rating,
  Divider,
  IconButton,
  Badge,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Breadcrumbs,
  Link,
  Alert,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  CircularProgress,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  ShoppingCart as CartIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  PhotoCamera as CameraIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Message as MessageIcon,
  Block as BlockIcon,
  Sell as SellIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  CalendarToday as CalendarTodayIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  Gavel as GavelIcon,
  Fullscreen as FullscreenIcon,
  Lock as LockIcon,
  Person as PersonAddIcon,
  Email as LoginIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ReviewModal from '../components/Reviews/ReviewModal';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import { ProductDetailService, ProductDetail } from '../services/productDetailService';
import { FavoritesService } from '../services/favoritesService';
import { Product } from '../types';
import { supabase } from '../lib/supabase';
import { DeliveryOptionsService, DeliveryOption } from '../services/deliveryOptionsService';
import SignInPromptModal from '../components/Auth/SignInPromptModal';
import JobApplicationModal from '../components/JobApplication/JobApplicationModal';
import BidPlacementPopup from '../components/Product/BidPlacementPopup';
import PhotoGalleryPopup from '../components/Product/PhotoGalleryPopup';
import AuthRequiredPopup from '../components/Auth/AuthRequiredPopup';
import BidPlacementHistoryPopup from '../components/Product/BidPlacementHistoryPopup';
import NewOfferPopup from '../components/Product/NewOfferPopup';
import ReportProductDialog from '../components/Product/ReportProductDialog';
import ShareDialog from '../components/Product/ShareDialog';
import { AuctionService } from '../services/auctionService';
import { PlatformSettingsService, PlatformFeeSettings } from '../services/platformSettingsService';
import { calculatePriceWithFeesSync } from '../utils/priceUtils';
import toast from 'react-hot-toast';
import { useTranslation } from '../contexts/TranslationContext';
import { translateText } from '../services/translationService';
import { ReportService, type ReportReason } from '../services/reportService';
import { cacheProductThumbnail } from '../utils/productThumbnailCache';

// Function to convert ProductDetail to Product for cart compatibility
const convertProductDetailToProduct = (productDetail: ProductDetail): Product => {
  return {
    id: productDetail.id,
    title: productDetail.title,
    description: productDetail.description,
    price: productDetail.price,
    originalPrice: productDetail.originalPrice,
    images: productDetail.images,
    category: productDetail.category,
    subcategory: productDetail.subcategory,
    brand: productDetail.brand,
    condition: productDetail.condition,
    material: productDetail.material,
    size: productDetail.size,
    color: productDetail.color,
    stock: productDetail.stats.stock,
    rating: productDetail.rating,
    reviewCount: productDetail.reviewCount,
    sellerId: productDetail.sellerId,
    seller: productDetail.seller,
    tags: productDetail.tags,
    createdAt: productDetail.createdAt,
    updatedAt: productDetail.updatedAt,
    isActive: productDetail.isActive,
    isJob: productDetail.isJob || false,
  };
};


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const sectionCardSx = {
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    bgcolor: 'background.paper',
  } as const;

  const sectionCardContentSx = {
    p: { xs: 2.5, sm: 3 },
  } as const;

  const { addItem } = useCartStore();
  const { isAuthenticated, user } = useAuthStore();
  const { 
    setMessagesSidebarOpen, 
    setPendingConversationTarget,
    setConversationsSidebarOpen,
    setConversationsSidebarProduct 
  } = useUIStore();
  
  const [productDetail, setProductDetail] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isOwnProduct, setIsOwnProduct] = useState(false);
  const [isUsingCache, setIsUsingCache] = useState(false);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewEditOpen, setReviewEditOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<any | null>(null);
  const [reviewRating, setReviewRating] = useState<number | null>(0);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewPriceRating, setReviewPriceRating] = useState(0);
  const [reviewAccuracyRating, setReviewAccuracyRating] = useState(0);
  const [reviewDeliveryRating, setReviewDeliveryRating] = useState(0);
  const [reviewPackagingRating, setReviewPackagingRating] = useState(0);
  const [reviewCommunicationRating, setReviewCommunicationRating] = useState(0);
  const [reviewQualityRating, setReviewQualityRating] = useState(0);
  const [reviewUserShowAlias, setReviewUserShowAlias] = useState(false);
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [platformFeeSettings, setPlatformFeeSettings] = useState<PlatformFeeSettings | null>(null);
  const [authRequiredOpen, setAuthRequiredOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shippingOptions, setShippingOptions] = useState<DeliveryOption[]>([]);
  const [selectedShippingOptionId, setSelectedShippingOptionId] = useState<string | null>(null);
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const { t, language, autoTranslate } = useTranslation();
  const [translatedDetail, setTranslatedDetail] = useState<{ title?: string; description?: string }>({});
  const [translatedProductReviews, setTranslatedProductReviews] = useState<Record<string | number, { title?: string; comment?: string }>>({});
  
  // Auction-related state
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isAuctionExpired, setIsAuctionExpired] = useState(false);
  // Live auction values for detail page (kept in sync with bids)
  const [latestAuction, setLatestAuction] = useState<{ id: string; current_bid: number; bid_count: number } | null>(null);
  
  // Offer popup state
  const [offerPopupOpen, setOfferPopupOpen] = useState(false);

  // Check if this is a job listing
  const isJob = productDetail?.isJob || false;

  // Handle application submission
  const handleApplicationSubmit = (applicationData: any) => {
    console.log('Application submitted:', applicationData);
    // The application has already been saved to the database by the modal
    // We just need to close the modal and show success message
    setApplicationModalOpen(false);
  };

  // Handle favorite toggle
  const handleToggleFavorite = async () => {
    // Check if user is authenticated
    if (!isAuthenticated || !user?.id || !productDetail?.id) {
      setSignInModalOpen(true);
      return;
    }
    
    // Prevent multiple rapid clicks
    if (favoritesLoading) return;
    
    setFavoritesLoading(true);
    
    try {
      // Toggle favorite in appropriate table based on job/product
      const isNowFavorited = isJob
        ? await FavoritesService.toggleJobFavorite(productDetail.id, user.id)
        : await FavoritesService.toggleFavorite(productDetail.id, user.id);
      
      // Update local state
      setIsFavorite(isNowFavorited);
      // Broadcast so lists can update their heart icons instantly
      window.dispatchEvent(new CustomEvent('favorite-changed', { detail: { id: productDetail.id, isFavorited: isNowFavorited, isJob } }));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      const err: any = error;
      const details = err?.message || err?.error_description || err?.details || err?.hint || String(err);
      toast.error(`Failed to update favorite: ${details}`);
    } finally {
      setFavoritesLoading(false);
    }
  };

  // Handle bid placement
  const handlePlaceBid = () => {
    if (!isAuthenticated || !user?.id) {
      setSignInModalOpen(true);
      return;
    }
    // Allow opening even if auction ended; overlay in modal will convey status
    setBidModalOpen(true);
  };

  // Handle bid placed successfully
  const handleBidPlaced = () => {
    // Refresh product data to get updated auction info
    if (productDetail?.id) {
      ProductDetailService.invalidateCache(productDetail.id);
      // Reload the product
      window.location.reload();
    }
  };

  // Load favorite status when product and user are available
  useEffect(() => {
    const loadFavoriteStatus = async () => {
      if (productDetail?.id && isAuthenticated && user?.id) {
        try {
          const isFavorited = isJob
            ? await FavoritesService.isJobFavorited(productDetail.id, user.id)
            : await FavoritesService.isProductFavorited(productDetail.id, user.id);
          setIsFavorite(isFavorited);
        } catch (error) {
          console.error('Error loading favorite status:', error);
        }
      }
    };

    loadFavoriteStatus();
  }, [productDetail?.id, isAuthenticated, user?.id]);

  // Fetch auction data when product is loaded
  useEffect(() => {
    const fetchAuctionData = async () => {
      if (!productDetail?.auctionId) return;

      try {
        const auctionData = await ProductDetailService.getAuctionData(productDetail.id);
        if (auctionData) {
          // Update the product detail with auction data
          setProductDetail(prev => prev ? {
            ...prev,
            auction: {
              id: auctionData.id,
              end_time: auctionData.end_time,
              current_bid: auctionData.current_bid,
              bid_increment: auctionData.bid_increment,
              status: auctionData.status,
              reserve_price: auctionData.reserve_price,
              bid_count: auctionData.bid_count,
              highest_bidder: undefined, // We can fetch this separately if needed
            }
          } : null);
        }
      } catch (error) {
        console.error('Error fetching auction data:', error);
      }
    };

    fetchAuctionData();
  }, [productDetail?.id, productDetail?.auctionId]);

  // Keep the displayed highest bid fresh on the detail page
  useEffect(() => {
    let isMounted = true;
    const refresh = async () => {
      if (!productDetail?.auction?.id) return;
      try {
        const bids = await AuctionService.getAuctionBids(productDetail.auction.id);
        const highest = bids && bids.length > 0 ? bids[0].bid_amount : (productDetail.auction.current_bid ?? productDetail.price);
        if (!isMounted) return;
        setLatestAuction({ id: productDetail.auction.id, current_bid: highest, bid_count: bids?.length ?? (productDetail.auction.bid_count || 0) });
      } catch (_) {
        // ignore background refresh failure
      }
    };
    refresh();
    return () => { isMounted = false; };
  }, [productDetail?.auction?.id]);

  useEffect(() => {
    if (!productDetail?.auction?.id) return;
    const channel = supabase
      .channel(`product-detail-auction-bids-${productDetail.auction.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'auction_bids', filter: `auction_id=eq.${productDetail.auction.id}` },
        async () => {
          try {
            const bids = await AuctionService.getAuctionBids(productDetail.auction!.id);
            const highest = bids && bids.length > 0 ? bids[0].bid_amount : (productDetail.auction!.current_bid ?? productDetail.price);
            setLatestAuction({ id: productDetail.auction!.id, current_bid: highest, bid_count: bids?.length ?? (productDetail.auction!.bid_count || 0) });
          } catch (_) {
            // ignore realtime refresh failure
          }
        }
      )
      .subscribe();
    return () => { try { supabase.removeChannel(channel); } catch {} };
  }, [productDetail?.auction?.id]);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setError('Product ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setIsUsingCache(false);
        
        console.log('ProductDetailPage - Fetching product with ID:', id);
        
        // Check if product is cached before fetching
        const isCached = ProductDetailService.isProductCached(id);
        if (isCached) {
          setIsUsingCache(true);
          console.log('ProductDetailPage - Product is cached, loading instantly');
        }
        
        const product = await ProductDetailService.getProductById(id);
        console.log('ProductDetailPage - Product result:', product);
        
        if (!product) {
          console.log('ProductDetailPage - Product not found for ID:', id);
          setError('Product not found');
          setLoading(false);
          return;
        }

        console.log('ProductDetailPage - Product found:', product);
        setProductDetail(product);

        try {
          await cacheProductThumbnail(product.id, Array.isArray((product as any).images) ? (product as any).images[0] : undefined);
        } catch {}
        
        // Check if this is the user's own product
        const { data: { user } } = await supabase.auth.getUser();
        const isUserOwnProduct = !!(user && product.sellerId === user.id);
        setIsOwnProduct(isUserOwnProduct);
        
        if (isUserOwnProduct) {
          console.log('ProductDetailPage - This is the user\'s own product');
        }
        
        // Increment view count only if it's not the user's own product
        if (!isUserOwnProduct) {
          try {
            await ProductDetailService.incrementViewCount(id);
          } catch (viewCountErr) {
            console.warn('Failed to increment view count:', viewCountErr);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product details');
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Load platform fee settings on mount
  useEffect(() => {
    const loadFeeSettings = async () => {
      try {
        const settings = await PlatformSettingsService.getPlatformFeeSettings();
        setPlatformFeeSettings(settings);
      } catch (error) {
        console.error('Error loading platform fee settings:', error);
        // Set default fee settings if loading fails
        setPlatformFeeSettings({
          fee_type: 'fixed',
          fee_amount: 0.50,
          fee_percentage: null,
        });
      }
    };
    loadFeeSettings();
  }, []);

  // Translate product title/description dynamically
  useEffect(() => {
    const run = async () => {
      try {
        if (!productDetail) { setTranslatedDetail({}); return; }
        if (!autoTranslate || language === 'en') { setTranslatedDetail({}); return; }
        const map: { title?: string; description?: string } = {};
        if (productDetail.title) map.title = await translateText(productDetail.title, language as any, 'en');
        if (productDetail.description) map.description = await translateText(productDetail.description, language as any, 'en');
        setTranslatedDetail(map);
      } catch {}
    };
    run();
  }, [productDetail, language, autoTranslate]);

  // Translate product reviews (title/comment)
  useEffect(() => {
    const run = async () => {
      try {
        if (!autoTranslate || language === 'en') { setTranslatedProductReviews({}); return; }
        if (!reviews || reviews.length === 0) { setTranslatedProductReviews({}); return; }
        const texts = new Set<string>();
        reviews.forEach(r => { if (r.title) texts.add(r.title); if (r.comment) texts.add(r.comment); });
        const map: Record<string, string> = {};
        await Promise.all(Array.from(texts).map(async (txt) => { map[txt] = await translateText(txt, language as any, 'en'); }));
        const out: Record<string | number, { title?: string; comment?: string }> = {};
        reviews.forEach(r => { out[r.id] = { title: r.title ? (map[r.title] || r.title) : undefined, comment: r.comment ? (map[r.comment] || r.comment) : undefined }; });
        setTranslatedProductReviews(out);
      } catch {}
    };
    run();
  }, [reviews, language, autoTranslate]);

  // Load reviews for this product
  useEffect(() => {
    const loadReviews = async () => {
      if (!productDetail?.id) return;
      try {
        setReviewsLoading(true);
        const { data, error } = await supabase
          .from('product_reviews')
          .select(`
            id,
            buyer_id,
            user_alias,
            rating,
            title,
            comment,
            created_at,
            images,
            price_rating,
            accuracy_rating,
            delivery_rating,
            packaging_rating,
            communication_rating,
            product_quality_rating,
            buyer:buyer_id ( alias )
          `)
          .eq('product_id', productDetail.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setReviews(data || []);
      } catch (e) {
        console.error('Failed to load reviews', e);
      } finally {
        setReviewsLoading(false);
      }
    };
    loadReviews();
  }, [productDetail?.id]);

  // Load shipping options for this product using delivery_option_ids
  useEffect(() => {
    const loadShipping = async () => {
      try {
        if (!productDetail?.delivery_option_ids || productDetail.delivery_option_ids.length === 0) {
          setShippingOptions([]);
          setSelectedShippingOptionId(null);
          return;
        }
        const all = await DeliveryOptionsService.getAll('DE');
        const filtered = all.filter(o => productDetail.delivery_option_ids?.includes(o.id));
        setShippingOptions(filtered);
      } catch (e) {
        console.error('Failed to load shipping options', e);
        setShippingOptions([]);
        setSelectedShippingOptionId(null);
      }
    };
    loadShipping();
  }, [productDetail?.delivery_option_ids]);

  // Default selected shipping option to cheapest available
  useEffect(() => {
    if (!shippingOptions || shippingOptions.length === 0) {
      setSelectedShippingOptionId(null);
      return;
    }
    // If current selection is still valid, keep it
    if (selectedShippingOptionId && shippingOptions.some(o => o.id === selectedShippingOptionId)) {
      return;
    }
    const cheapest = shippingOptions
      .filter(o => typeof o.price_eur === 'number' && !isNaN(o.price_eur as any))
      .sort((a, b) => (Number(a.price_eur) || 0) - (Number(b.price_eur) || 0))[0];
    setSelectedShippingOptionId(cheapest?.id || shippingOptions[0].id);
  }, [shippingOptions, selectedShippingOptionId]);

  // Compute overall rating from category ratings when editing
  useEffect(() => {
    const parts = [
      reviewPriceRating,
      reviewAccuracyRating,
      reviewDeliveryRating,
      reviewPackagingRating,
      reviewCommunicationRating,
      reviewQualityRating,
    ].filter(v => v > 0);
    if (parts.length > 0) {
      const avg = parts.reduce((a, b) => a + b, 0) / parts.length;
      const rounded = Math.round(avg * 10) / 10;
      setReviewRating(rounded);
    }
  }, [reviewPriceRating, reviewAccuracyRating, reviewDeliveryRating, reviewPackagingRating, reviewCommunicationRating, reviewQualityRating]);

  // Calculate auction time remaining (with seconds, real-time)
  useEffect(() => {
    if (!productDetail?.auction?.end_time) return;

    const calculateTimeRemaining = () => {
      const now = new Date();
      const end = new Date(productDetail.auction!.end_time);
      const diffMs = end.getTime() - now.getTime();
      
      if (diffMs <= 0) {
        setIsAuctionExpired(true);
        setTimeRemaining('Auction Ended');
        return;
      }
      
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      const two = (n: number) => n.toString().padStart(2, '0');

      const label = `${days > 0 ? days + 'd ' : ''}${two(hours)}:${two(minutes)}:${two(seconds)}`;
      setTimeRemaining(label);
      setIsAuctionExpired(false);
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    
    return () => clearInterval(interval);
  }, [productDetail?.auction?.end_time]);

  // Debug company data
  useEffect(() => {
    if (productDetail) {
      console.log('ProductDetail company data:', productDetail.company);
      console.log('Is job:', isJob);
    }
  }, [productDetail, isJob]);

  const openEditReview = (review: any) => {
    setReviewTarget(review);
    setReviewTitle(review.title || '');
    setReviewComment(review.comment || '');
    setReviewPriceRating(review.price_rating || 0);
    setReviewAccuracyRating(review.accuracy_rating || 0);
    setReviewDeliveryRating(review.delivery_rating || 0);
    setReviewPackagingRating(review.packaging_rating || 0);
    setReviewCommunicationRating(review.communication_rating || 0);
    setReviewQualityRating(review.product_quality_rating || 0);
    setReviewRating(review.rating || 0);
    setReviewUserShowAlias(!!review.user_showalias);
    setReviewEditOpen(true);
  };

  const submitReviewUpdate = async () => {
    if (!reviewTarget) return;
    try {
      setReviewSubmitting(true);
      const { data: aliasRow } = await supabase
        .from('users')
        .select('alias')
        .eq('id', user?.id || '')
        .single();

      const { error } = await supabase
        .from('product_reviews')
        .update({
          rating: reviewRating,
          title: reviewTitle || null,
          comment: reviewComment || null,
          user_showalias: reviewUserShowAlias,
          user_alias: reviewUserShowAlias ? (aliasRow?.alias || null) : null,
          price_rating: reviewPriceRating || null,
          accuracy_rating: reviewAccuracyRating || null,
          delivery_rating: reviewDeliveryRating || null,
          packaging_rating: reviewPackagingRating || null,
          communication_rating: reviewCommunicationRating || null,
          product_quality_rating: reviewQualityRating || null,
        })
        .eq('id', reviewTarget.id);
      if (error) throw error;
      // refresh list
      setReviewEditOpen(false);
      setReviewTarget(null);
      const { data } = await supabase
        .from('product_reviews')
        .select('*')
        .eq('product_id', productDetail?.id)
        .order('created_at', { ascending: false });
      setReviews(data || []);
    } catch (e) {
      console.error('Failed to update review', e);
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              {isUsingCache ? 'Loading cached data...' : 'Loading product details...'}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Handle error state
  if (error || !productDetail) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {error || 'Product Not Found'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The product you're looking for doesn't exist or has been removed.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/all-products')}
              startIcon={<BackIcon />}
            >
              {t('productDetail.buttons.back')}
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const product = convertProductDetailToProduct(productDetail);
  console.log('ProductDetailPage - productDetail.images:', productDetail.images);
  const productImages = productDetail.images.length > 0 ? productDetail.images : ['/placeholder-product.jpg'];
  console.log('ProductDetailPage - productImages:', productImages);
  const isSold = (productDetail as any)?.isSold || false;
  const isUnavailable = isSold || !productDetail?.isActive;

  const ptRaw = (productDetail as any)?.productType ?? (productDetail as any)?.product_type;
  const ptNorm = (() => {
    const s = String(ptRaw ?? '').trim().toLowerCase();
    if (s === 'd' || s.includes('direkt')) return 'd';
    if (s === 'p' || s.includes('angebot') || s.includes('offer')) return 'p';
    if (s === 'a' || s.includes('auktion') || s.includes('auction')) return 'a';
    return '';
  })();
  const isNegotiableProduct = Boolean((productDetail as any)?.is_negotiable);
  const isOfferProduct = isNegotiableProduct || ptNorm === 'p' || (!ptNorm && Boolean(productDetail?.is_private_singlebuy));

  const handleAddToCart = async () => {
    // Keep cart pricing consistent with what we display on this page (incl. platform fees)
    const basePrice = product.price || 0;
    const displayPrice = (!isJob && platformFeeSettings)
      ? calculatePriceWithFeesSync(basePrice, platformFeeSettings)
      : basePrice;

    const baseOriginalPrice = product.originalPrice;
    const displayOriginalPrice = (!isJob && baseOriginalPrice && platformFeeSettings)
      ? calculatePriceWithFeesSync(baseOriginalPrice, platformFeeSettings)
      : baseOriginalPrice;

    let shippingPerItem = 0;

    const selectedOpt = selectedShippingOptionId
      ? shippingOptions.find(o => o.id === selectedShippingOptionId)
      : null;
    if (selectedOpt && typeof selectedOpt.price_eur === 'number' && !isNaN(selectedOpt.price_eur)) {
      shippingPerItem = selectedOpt.price_eur;
    }

    if (shippingPerItem <= 0 && Array.isArray(productDetail.delivery_option_ids) && productDetail.delivery_option_ids.length > 0) {
      try {
        const all = await DeliveryOptionsService.getAll('DE');
        const filtered = all.filter((o) => productDetail.delivery_option_ids?.includes(o.id));
        const cheapest = filtered
          .filter(o => typeof o.price_eur === 'number' && !isNaN(o.price_eur as any))
          .sort((a, b) => (Number(a.price_eur) || 0) - (Number(b.price_eur) || 0))[0];
        shippingPerItem = typeof cheapest?.price_eur === 'number' && !isNaN(cheapest.price_eur) ? cheapest.price_eur : 0;
      } catch {
        // ignore
      }
    }

    addItem(
      {
        ...product,
        price: displayPrice,
        originalPrice: displayOriginalPrice,
      },
      quantity,
      selectedSize || undefined,
      selectedColor || undefined,
      shippingPerItem
    );
    toast.success(`${product.title} added to cart!`, { position: 'top-center' });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleImageClick = () => {
    if (!isAuthenticated) {
      setAuthRequiredOpen(true);
    } else {
      setPhotoGalleryOpen(true);
    }
  };

  const handleOpenReport = () => {
    if (!isAuthenticated || !user?.id) {
      setAuthRequiredOpen(true);
      return;
    }
    setReportDialogOpen(true);
  };

  const handleSubmitReport = async (payload: { subject: string; reason: ReportReason; message: string }) => {
    if (!isAuthenticated || !user?.id) {
      setAuthRequiredOpen(true);
      return;
    }
    if (!productDetail?.id || !productDetail?.sellerId) {
      toast.error('Product data not loaded yet. Please try again.');
      return;
    }

    try {
      await ReportService.submitProductReport({
        reporterUserId: user.id,
        sellerUserId: productDetail.sellerId,
        productId: productDetail.id,
        productTitle: productDetail.title,
        productUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        subject: payload.subject,
        reason: payload.reason,
        message: payload.message,
      });
      toast.success('Thanks — your report has been submitted.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to submit report');
      return;
    }
  };

  const renderImageGallery = () => (
    <Card sx={sectionCardSx}>
      <CardContent sx={sectionCardContentSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Main Image */}
          <Box
            sx={{
              width: '100%',
              height: 500,
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              '&:hover': isAuthenticated ? {
                transform: 'scale(1.02)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
              } : {},
            }}
          >
        {productImages.length > 0 ? (
          <>
          <motion.img
            key={selectedImage}
            src={productImages[selectedImage]}
            alt={product.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: isUnavailable ? 'grayscale(100%)' : 'none',
              cursor: isAuthenticated ? 'pointer' : 'not-allowed',
              opacity: isAuthenticated ? 1 : 0.7,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            onClick={handleImageClick}
            onError={(e) => {
              console.error('Image failed to load:', productImages[selectedImage]);
              e.currentTarget.src = '/placeholder-product.jpg';
            }}
            onLoad={() => {
              console.log('Image loaded successfully:', productImages[selectedImage]);
            }}
          />
          {/* Click to expand overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: isAuthenticated ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.3)',
              borderRadius: '50%',
              p: 1,
              cursor: isAuthenticated ? 'pointer' : 'not-allowed',
              opacity: isAuthenticated ? 0.8 : 0.5,
              transition: 'all 0.2s ease',
              '&:hover': isAuthenticated ? {
                opacity: 1,
                bgcolor: 'rgba(0,0,0,0.8)',
                transform: 'scale(1.1)',
              } : {},
            }}
            onClick={handleImageClick}
          >
            {isAuthenticated ? (
              <FullscreenIcon sx={{ color: 'white', fontSize: 20 }} />
            ) : (
              <LockIcon sx={{ color: 'white', fontSize: 20 }} />
            )}
          </Box>

          {/* Authentication required overlay for unauthenticated users */}
          {!isAuthenticated && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                right: 16,
                bgcolor: 'rgba(0,0,0,0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <LockIcon sx={{ color: 'white', fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  flex: 1,
                }}
              >
                Sign in to view all photos
              </Typography>
            </Box>
          )}
          {isUnavailable && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: 'rgba(255,255,255,0.92)',
                  px: 2.5,
                  py: 1,
                  borderRadius: 999,
                  boxShadow: 2,
                }}
              >
                {isSold ? (
                  <SellIcon sx={{ color: 'text.secondary' }} />
                ) : (
                  <BlockIcon sx={{ color: 'text.secondary' }} />
                )}
                <Typography fontWeight="bold" color="text.primary">
                  {isSold ? 'Sold' : 'Inactive'}
                </Typography>
              </Box>
            </Box>
          )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <CameraIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">No images available</Typography>
            <Typography variant="body2">Images will appear here when uploaded</Typography>
          </Box>
        )}
        
        {/* Image Navigation */}
        {productImages.length > 1 && (
          <>
            <IconButton
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.8)',
                '&:hover': { bgcolor: 'white' },
              }}
              onClick={() => setSelectedImage(Math.max(0, selectedImage - 1))}
              disabled={selectedImage === 0}
            >
              <BackIcon />
            </IconButton>
            <IconButton
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.8)',
                '&:hover': { bgcolor: 'white' },
              }}
              onClick={() => setSelectedImage(Math.min(productImages.length - 1, selectedImage + 1))}
              disabled={selectedImage === productImages.length - 1}
            >
              <BackIcon sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>
          </>
        )}
          </Box>

          {/* Thumbnail Images */}
          {productImages.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto' }}>
              {productImages.map((image, index) => (
                <Box
                  key={index}
                  onClick={() => {
                    setSelectedImage(index);
                    handleImageClick();
                  }}
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: isAuthenticated ? 'pointer' : 'not-allowed',
                    border: selectedImage === index ? 3 : 1,
                    borderColor: selectedImage === index ? 'primary.main' : 'grey.300',
                    flexShrink: 0,
                    transition: 'all 0.2s ease',
                    opacity: isAuthenticated ? 1 : 0.6,
                    '&:hover': isAuthenticated ? {
                      transform: 'scale(1.05)',
                      borderColor: 'primary.main',
                    } : {},
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt={`${product.title} ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: isUnavailable ? 'grayscale(100%)' : 'none',
                      opacity: isUnavailable ? 0.8 : 1,
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderProductInfo = () => {
    return (
      <Card
        sx={{
          ...sectionCardSx,
          ...(isOwnProduct && {
            border: '2px solid rgba(16, 185, 129, 0.35)',
            backgroundColor: 'rgba(16, 185, 129, 0.03)',
          }),
        }}
      >
        <CardContent sx={sectionCardContentSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Breadcrumbs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Breadcrumbs sx={{ flex: 1, minWidth: 0 }}>
            <Link component="button" variant="body2" onClick={() => navigate(isJob ? '/all-jobs' : '/all-products')} sx={{ textDecoration: 'none' }}>
              {isJob ? t('productDetail.breadcrumb.jobs') : t('productDetail.breadcrumb.products')}
            </Link>
            {(() => {
            // Prefer first categories[] entry to build full path (main / sub / sub-sub)
            const first = Array.isArray(productDetail.categories) && productDetail.categories.length > 0 ? productDetail.categories[0] : '';
            const segs = typeof first === 'string' ? first.split(' / ').map(s => s.trim()).filter(Boolean) : [];
            const main = segs[0] || ((productDetail.category && productDetail.category !== 'Unknown') ? productDetail.category : '');
            const sub = segs[1] || ((productDetail.subcategory && productDetail.subcategory !== 'Unknown') ? productDetail.subcategory : '');
            const subsub = segs[2] || '';
            const items: React.ReactNode[] = [];
            if (main) {
              items.push(
                <Link
                  key="bc-main"
                  component="button"
                  variant="body2"
                  onClick={() => navigate(isJob ? `/all-jobs?category=${encodeURIComponent(main.toLowerCase())}` : `/all-products?category=${encodeURIComponent(main.toLowerCase())}`)}
                  sx={{ textDecoration: 'none' }}
                >
                  {main}
                </Link>
              );
            }
            if (sub) {
              items.push(
                <Link
                  key="bc-sub"
                  component="button"
                  variant="body2"
                  onClick={() => navigate(isJob ? `/all-jobs?category=${encodeURIComponent(main)}&sub=${encodeURIComponent(sub)}` : `/all-products?category=${encodeURIComponent(main)}&sub=${encodeURIComponent(sub)}`)}
                  sx={{ textDecoration: 'none' }}
                >
                  {sub}
                </Link>
              );
            }
            if (subsub) {
              items.push(
                <Link
                  key="bc-subsub"
                  component="button"
                  variant="body2"
                  onClick={() => navigate(isJob ? `/all-jobs?category=${encodeURIComponent(main)}&sub=${encodeURIComponent(sub)}&subsub=${encodeURIComponent(subsub)}` : `/all-products?category=${encodeURIComponent(main)}&sub=${encodeURIComponent(sub)}&subsub=${encodeURIComponent(subsub)}`)}
                  sx={{ textDecoration: 'none' }}
                >
                  {subsub}
                </Link>
              );
            }
              return items;
            })()}
            <Typography variant="body2" color="text.primary">
              {translatedDetail.title || product.title}
            </Typography>
          </Breadcrumbs>
        </Box>

      {/* Job/Product Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flex: 1, minWidth: 0 }}>
          {translatedDetail.title || product.title}
        </Typography>
        {isAuthenticated && isOwnProduct && (
          <Chip
            label={isJob ? t('productDetail.status.yourJob') : t('productDetail.status.yourProduct')}
            color="success"
            variant="filled"
            icon={<CheckIcon />}
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
              height: 32,
              '& .MuiChip-icon': {
                fontSize: 18,
              },
            }}
          />
        )}
        {/* Status labels */}
        {!productDetail.isActive && (
          <Chip
            label={t('productDetail.status.inactive')}
            variant="filled"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
              bgcolor: 'grey.300',
              color: 'grey.900',
              height: 32,
            }}
          />
        )}
        {isSold && !isJob && (
          <Chip
            label={t('productDetail.status.sold')}
            variant="filled"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
              bgcolor: 'grey.300',
              color: 'grey.900',
              height: 32,
            }}
          />
        )}
        {isJob && (
          <Chip
            label={t('productDetail.status.active')}
            color="success"
            variant="filled"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
              height: 32,
            }}
          />
        )}
        {productDetail.auctionId && productDetail.auction && (
          <Chip
            label={t('productDetail.status.auction')}
            color="warning"
            variant="filled"
            icon={<GavelIcon />}
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
              height: 32,
            }}
          />
        )}
        {isAuthenticated && isOfferProduct && !isJob && !(productDetail.auctionId && productDetail.auction) && (
          <Chip
            label={(isSold || !productDetail.isActive) ? t('productDetail.status.privateSale') : t('productDetail.status.privateSaleMakeOffer')}
            color="info"
            variant="outlined"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.9rem',
              borderColor: '#667eea',
              color: '#667eea',
              height: 32,
            }}
          />
        )}
      </Box>

      {/* Rating and Reviews */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Rating value={product.rating} precision={0.1} readOnly />
        <Typography variant="body2" color="text.secondary">
          {product.rating} ({product.reviewCount} {t('productDetail.rating.reviews')})
        </Typography>
        <Chip
          label={`${product.reviewCount} ${t('productDetail.rating.reviews')}`}
          size="small"
          color="success"
          variant="outlined"
        />
      </Box>

      {/* Mobile: show image gallery inside the info flow */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
        {renderImageGallery()}
      </Box>

      {/* All Categories for this Product */}
      {(() => {
        const paths: string[] = Array.isArray(productDetail.categories) ? productDetail.categories : [];
        const display = paths.filter(p => typeof p === 'string' && p.trim().length > 0);
        if (display.length === 0) return null;
        const rawTitle = t('productDetail.categories.title');
        const title = rawTitle && rawTitle !== 'productDetail.categories.title' ? rawTitle : 'Categories';
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {display.map((p, idx) => {
                const segs = p.split(' / ').map(s => s.trim()).filter(Boolean);
                const [main, sub, subsub] = [segs[0] || '', segs[1] || '', segs[2] || ''];
                const to = `/all-products?category=${encodeURIComponent((main || '').toLowerCase())}`
                  + (sub ? `&sub=${encodeURIComponent(sub)}` : '')
                  + (subsub ? `&subsub=${encodeURIComponent(subsub)}` : '');
                return (
                  <Chip
                    key={`cat-path-${idx}`}
                    label={p}
                    variant="outlined"
                    onClick={() => navigate(to)}
                    clickable
                    sx={{
                      borderRadius: 999,
                      fontWeight: 700,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'primary.50',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        );
      })()}

      {/* Price / Salary / Auction */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {productDetail.auctionId && productDetail.auction && !isJob ? (
          <>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('productDetail.auction.currentBid')}
              </Typography>
              <Typography
                variant="h3"
                fontWeight="bold"
                color={isUnavailable ? 'text.disabled' : 'primary.main'}
                sx={{ cursor: 'pointer' }}
                onClick={() => setHistoryOpen(true)}
              >
                ${((latestAuction?.current_bid ?? productDetail.auction.current_bid)).toFixed(2)}
              </Typography>
            </Box>
            <Chip
              label={timeRemaining || (isAuctionExpired ? t('productDetail.auction.ended') : t('productDetail.loading'))}
              icon={<ScheduleIcon />}
              onClick={handlePlaceBid}
              sx={{
                fontWeight: 'bold',
                borderRadius: 999,
                px: 2.5,
                height: 56,
                color: '#fff',
                background: isAuctionExpired
                  ? 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)'
                  : 'linear-gradient(135deg, #ffcc80 0%, #ffb74d 100%)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                '& .MuiChip-icon': { color: '#fff', fontSize: 24 },
                fontSize: '1.25rem',
                cursor: 'pointer'
              }}
            />
          </>
        ) : (
          <>
            {(() => {
              // Calculate price with fees (only for products, not jobs)
              const basePrice = product.price || 0;
              const displayPrice = (!isJob && platformFeeSettings)
                ? calculatePriceWithFeesSync(basePrice, platformFeeSettings)
                : basePrice;
              const baseOriginalPrice = product.originalPrice;
              const displayOriginalPrice = (!isJob && baseOriginalPrice && platformFeeSettings)
                ? calculatePriceWithFeesSync(baseOriginalPrice, platformFeeSettings)
                : baseOriginalPrice;
              
              return (
                <>
                  <Typography variant="h3" fontWeight="bold" color={isUnavailable ? 'text.disabled' : 'primary.main'}>
                    {isJob
                      ? (displayPrice > 0 ? `$${displayPrice.toFixed(2)}/hr` : 'Salary discussed in interview')
                      : `$${displayPrice.toFixed(2)}`}
                  </Typography>
                  {!isJob && displayOriginalPrice && (
                    <Typography
                      variant="h6"
                      color={isUnavailable ? 'text.disabled' : 'text.secondary'}
                      sx={{ textDecoration: 'line-through' }}
                    >
                      ${displayOriginalPrice.toFixed(2)}
                    </Typography>
                  )}
                  {!isJob && displayOriginalPrice && (
                    <Chip
                      label={`${Math.round(((displayOriginalPrice - displayPrice) / displayOriginalPrice) * 100)}% OFF`}
                      size="small"
                      sx={isUnavailable ? {
                        bgcolor: 'grey.300',
                        color: 'grey.700',
                        fontWeight: 600,
                      } : {}}
                      color={isUnavailable ? undefined : 'error'}
                    />
                  )}
                </>
              );
            })()}
            {isJob && (
              <Chip
                label={(product?.price ?? 0) > 0 ? 'Competitive' : 'Discussed in interview'}
                color="success"
                variant="outlined"
                size="small"
                sx={{
                  fontWeight: 'bold',
                }}
              />
            )}
          </>
        )}
      </Box>

      {/* Description */}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {translatedDetail.description || product.description || t('productDetail.description.fallback')}
      </Typography>

      {/* Tags/Skills */}
      {product.tags && product.tags.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {isJob ? t('productDetail.requiredSkills') : t('productDetail.features')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {product.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                variant="outlined"
                color={isJob ? "secondary" : "primary"}
                size="small"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Quantity/Job Info */}
      {isJob ? (
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {t('productDetail.job.details')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Chip
              label={t('productDetail.job.positions').replace('{count}', String(productDetail.stats.stock || 1))}
              color="info"
              variant="outlined"
              icon={<LocationIcon />}
            />
            <Chip
              label={t('productDetail.job.fullTime')}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={t('productDetail.job.remoteOk')}
              color="success"
              variant="outlined"
            />
          </Box>
        </Box>
      ) : (
        <Box>
          {isOfferProduct ? (
            <Box>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t('productDetail.cta.makeOffer')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('productDetail.sendVendorMessage')}
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {t('productDetail.quantity.title')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </Button>
                <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>
                  {quantity}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  +
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {productDetail.stats.stock > 0 ? t('productDetail.stock.inStock').replace('{count}', String(productDetail.stats.stock)) : t('productDetail.stock.outOfStock')}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
        {isAuthenticated && isOwnProduct ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 2,
                px: 3,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              }}
            >
              <CheckIcon sx={{ mr: 1, fontSize: 24 }} />
              {isJob ? t('productDetail.status.yourJob') : t('productDetail.status.yourProduct')}
            </Box>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(isJob ? `/edit-job/${productDetail.id}` : `/edit-product/${productDetail.id}`, {
                state: { fromProductDetail: true }
              })}
              sx={{ 
                py: 1.5,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'primary.50',
                }
              }}
            >
              {isJob ? t('productDetail.buttons.editJob') : t('productDetail.buttons.editProduct')}
            </Button>
            {(!isJob && productDetail.auctionId && productDetail.auction) && (
              <Button
                variant="contained"
                size="large"
                startIcon={<GavelIcon />}
                onClick={handlePlaceBid}
                sx={{
                  background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)' },
                  py: 1.7,
                  px: 3,
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  boxShadow: '0 6px 16px rgba(255, 152, 0, 0.35)'
                }}
              >
                View Bids
              </Button>
            )}
          </Box>
        ) : (
          <>
            {!isJob && (
              <>
                {/* Auction UI - Row 1 */}
                {productDetail.auctionId && productDetail.auction ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Auction Info Card */}
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
                      border: '2px solid #f44336',
                      borderRadius: 2,
                      p: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold" color="error.main">
                          🔨 Live Auction
                        </Typography>
                        <Chip
                          label={timeRemaining}
                          icon={<ScheduleIcon />}
                          onClick={handlePlaceBid}
                          sx={{
                            fontWeight: 'bold',
                            borderRadius: 999,
                            px: 2.5,
                            height: 64,
                            color: '#fff',
                            background: isAuctionExpired
                              ? 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)'
                              : 'linear-gradient(135deg, #ffcc80 0%, #ffb74d 100%)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                            '& .MuiChip-icon': { color: '#fff', fontSize: 28 },
                            fontSize: '2rem',
                            alignItems: 'center',
                            cursor: 'pointer'
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Current Highest Bid
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary.main">
                            ${((latestAuction?.current_bid ?? productDetail.auction.current_bid)).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary">
                            {(latestAuction?.bid_count ?? productDetail.auction.bid_count)} bid{(latestAuction?.bid_count ?? productDetail.auction.bid_count) !== 1 ? 's' : ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            +${productDetail.auction.bid_increment.toFixed(2)} increment
                          </Typography>
                        </Box>
                      </Box>

                      {productDetail.auction.reserve_price && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Reserve Price: ${productDetail.auction.reserve_price.toFixed(2)}
                            {productDetail.auction.current_bid >= productDetail.auction.reserve_price ? 
                              ' ✅ Met' : 
                              ` (Need $${(productDetail.auction.reserve_price - productDetail.auction.current_bid).toFixed(2)} more)`
                            }
                          </Typography>
                        </Box>
                      )}

                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<GavelIcon />}
                        onClick={handlePlaceBid}
                        sx={{
                          background: 'linear-gradient(135deg, #ff9800 0%, #fb8c00 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                          },
                          py: 1.7,
                          px: 3,
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                          boxShadow: '0 6px 16px rgba(255, 152, 0, 0.35)'
                        }}
                      >
                        View Bids
                      </Button>
                    </Card>
                  </Box>
                ) : (
                  /* Regular Product UI - Row 1 */
                  isAuthenticated ? (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={isOfferProduct ? <MessageIcon /> : <CartIcon />}
                      onClick={isOfferProduct ? () => {
                        setOfferPopupOpen(true);
                      } : handleAddToCart}
                      disabled={productDetail.stats.stock === 0}
                      sx={(t) => ({
                        flex: 1,
                        py: 1.5,
                        borderRadius: 999,
                        fontWeight: 800,
                        textTransform: 'none',
                        ...(isOfferProduct
                          ? {
                              backgroundColor: t.palette.primary.main,
                              '&:hover': { backgroundColor: t.palette.primary.dark },
                            }
                          : {
                              backgroundColor: t.palette.primary.main,
                              '&:hover': { backgroundColor: t.palette.primary.dark },
                            }),
                      })}
                    >
                      {productDetail.stats.stock === 0 
                        ? t('productDetail.cta.outOfStock') 
                        : isOfferProduct
                          ? t('productDetail.cta.makeOffer') 
                          : t('productDetail.cta.addToCart')
                      }
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<LockIcon />}
                      onClick={() => setAuthRequiredOpen(true)}
                      sx={{
                        flex: 1,
                        py: 1.5,
                        borderRadius: 999,
                        fontWeight: 800,
                        textTransform: 'none',
                        backgroundColor: 'grey.600',
                        '&:hover': { backgroundColor: 'grey.700' },
                      }}
                    >
                      {t('productDetail.cta.signInTo').replace('{action}', isOfferProduct ? t('productDetail.cta.makeOffer') : t('productDetail.cta.addToCart'))}
                    </Button>
                  )
                )}
                {/* Row 2: Send Vendor Message */}
                {isAuthenticated ? (
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<MessageIcon />}
                    onClick={() => {
                      // Set the conversation target with product details
                      setPendingConversationTarget({
                        receiverId: productDetail.sellerId,
                        productId: productDetail.id,
                        subject: `Inquiry about ${productDetail.title}`
                      });
                      // Navigate immediately so the user can start chatting without waiting
                      navigate('/messages', {
                        state: {
                          from: { path: `/product/${productDetail.id}`, title: 'Product' },
                        }
                      });
                    }}
                    sx={(t) => ({
                      py: 1.5,
                      px: 3,
                      borderRadius: 999,
                      borderColor: t.palette.primary.main,
                      color: t.palette.primary.main,
                      fontWeight: 800,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: t.palette.primary.dark,
                        backgroundColor: t.palette.action.hover,
                      },
                    })}
                  >
                    {t('productDetail.sendVendorMessage')}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<LockIcon />}
                    onClick={() => setAuthRequiredOpen(true)}
                    sx={(t) => ({
                      py: 1.5,
                      px: 3,
                      borderRadius: 999,
                      borderColor: t.palette.grey[400],
                      color: t.palette.grey[600],
                      fontWeight: 800,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: t.palette.grey[600],
                        backgroundColor: t.palette.action.hover,
                      },
                    })}
                  >
                    {t('productDetail.signInToContact')}
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* Authentication Required for Buying/Contacting */}
      {!isAuthenticated && (
        <Card sx={{ 
          borderRadius: 3, 
          mt: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)
              `,
            }}
          />
          
          <CardContent sx={{ position: 'relative', zIndex: 1, p: 4 }}>
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              {/* Icon */}
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: '2px solid rgba(255,255,255,0.2)',
                }}
              >
                <LockIcon sx={{ fontSize: 30, color: 'white' }} />
              </Box>

              {/* Title */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(45deg, #fff 30%, #f0f0f0 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('productDetail.cta.signInTo').replace('{action}', isJob ? t('productDetail.cta.apply') : t('productDetail.cta.buy'))} {t('productDetail.cta.contactSeller')}
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  maxWidth: 400,
                  mx: 'auto',
                }}
              >
                {isJob 
                  ? t('productDetail.cta.signInTo').replace('{action}', t('productDetail.cta.apply'))
                  : t('productDetail.cta.signInTo').replace('{action}', t('productDetail.cta.buy'))
                }
              </Typography>

              {/* Benefits */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  mb: 3,
                  maxWidth: 300,
                  mx: 'auto',
                }}
              >
                {[
                  { icon: <CartIcon />, text: isJob ? t('productDetail.cta.apply') : t('productDetail.cta.buy') },
                  { icon: <MessageIcon />, text: t('productDetail.cta.contactSeller') },
                  { icon: <StarIcon />, text: 'Save favorites & track orders' },
                ].map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}
                  >
                    <Box sx={{ color: 'white', opacity: 0.9 }}>
                      {benefit.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        fontWeight: 500,
                        opacity: 0.9,
                      }}
                    >
                      {benefit.text}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                }}
              >
                <Button
                  onClick={() => navigate('/signup')}
                  variant="contained"
                  size="large"
                  startIcon={<PersonAddIcon />}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
                    },
                    transition: 'all 0.2s ease',
                    minWidth: { xs: '100%', sm: 140 },
                  }}
                >
                  {t('auth.signup') || 'Sign Up'}
                </Button>

                <Button
                  onClick={() => setSignInModalOpen(true)}
                  variant="outlined"
                  size="large"
                  startIcon={<LoginIcon />}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease',
                    minWidth: { xs: '100%', sm: 140 },
                  }}
                >
                  {t('auth.signin') || 'Sign In'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Shipping Options */}
      <Card sx={{ borderRadius: 2, boxShadow: 1, mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ShippingIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">{t('productDetail.shipping.title')}</Typography>
          </Box>
          {shippingOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('productDetail.shipping.none')}</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              {shippingOptions.map(opt => {
                const selected = selectedShippingOptionId === opt.id;
                return (
                <Paper
                  key={opt.id}
                  onClick={() => setSelectedShippingOptionId(opt.id)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.18s ease',
                    boxShadow: selected ? '0 10px 28px rgba(124,58,237,0.16)' : 'none',
                    background: selected
                      ? 'linear-gradient(180deg, rgba(124,58,237,0.08) 0%, rgba(255,255,255,0.96) 100%)'
                      : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">{opt.carrier} • {opt.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {opt.max_weight_kg ? `${opt.max_weight_kg} kg max` : ''}
                    {(opt.max_length_cm || opt.max_width_cm || opt.max_height_cm) ?
                      ` • ${opt.max_length_cm || '-'}×${opt.max_width_cm || '-'}×${opt.max_height_cm || '-'} cm` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                    <Chip size="small" label={opt.price_eur != null ? `€${opt.price_eur.toFixed(2)}` : '—'} />
                    {selected && (
                      <Chip size="small" color="primary" label="Selected" />
                    )}
                  </Box>
                </Paper>
              );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderSellerInfo = () => (
    <Card sx={sectionCardSx}>
      <CardContent sx={sectionCardContentSx}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('productDetail.seller.title')}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={productDetail.seller.avatar}
            sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}
          >
            {productDetail.seller.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {productDetail.seller.name}
              </Typography>
              {productDetail.seller.verified && (
                <VerifiedIcon color="primary" fontSize="small" />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Rating value={productDetail.seller.rating} precision={0.1} size="small" readOnly />
              <Typography variant="body2" color="text.secondary">
                {productDetail.seller.rating} ({t('productDetail.seller.sales').replace('{count}', String(productDetail.seller.reviewCount))})
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {productDetail.seller.verified ? t('productDetail.seller.verified') : t('productDetail.seller.role.seller')}
              {productDetail.seller.location && ` • ${productDetail.seller.location}`}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('productDetail.seller.trustedBlurb')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {productDetail.seller.verified && (
            <Chip
              icon={<CheckIcon />}
              label={t('productDetail.seller.verified')}
              color="success"
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            icon={<InfoIcon />}
            label={t('productDetail.seller.memberSince').replace('{year}', String(new Date(productDetail.seller.joinDate).getFullYear()))}
            size="small"
            variant="outlined"
          />
        </Box>

        <Button
          variant="outlined"
          fullWidth
          startIcon={<StoreIcon />}
          onClick={() => navigate(`/seller/${productDetail.seller.id}`)}
        >
          {t('productDetail.seller.viewStore')}
        </Button>
      </CardContent>
    </Card>
  );

  const renderReviews = () => (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
        Customer Reviews
      </Typography>

      {/* Review Summary (computed from loaded reviews) */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="primary.main">
                {reviews.length > 0 ? (reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length).toFixed(1) : '0.0'}
              </Typography>
              <Rating value={reviews.length > 0 ? (reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length) : 0} precision={0.1} readOnly />
              <Typography variant="body2" color="text.secondary">
                Based on {reviews.length} review{reviews.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              {[5, 4, 3, 2, 1].map((star) => {
                const count = reviews.filter(r => Math.round(r.rating) === star).length;
                const pct = reviews.length ? (count / reviews.length) * 100 : 0;
                return (
                  <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2">{star}</Typography>
                    <StarIcon fontSize="small" color="primary" />
                    <Box sx={{ flex: 1, height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', bgcolor: 'primary.main', width: `${pct}%` }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">{count}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Individual Reviews */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {reviewsLoading ? (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="30%" />
                  <Skeleton variant="text" width="20%" />
                </Box>
              </Box>
              <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        ) : reviews.length > 0 ? (
          reviews.map((review) => (
            <Card key={review.id} sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar src={'/placeholder-avatar.jpg'} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {review?.user_showalias && review?.user_alias ? review.user_alias : 'Buyer'}
                      </Typography>
                      {/* Edit button for own review */}
                      {isAuthenticated && user?.id && review.buyer_id === user.id && (
                        <>
                          <Chip
                            icon={<CheckIcon />}
                            label="Your review"
                            color="success"
                            size="small"
                            sx={{ ml: 0.5 }}
                          />
                          <IconButton size="small" onClick={() => openEditReview(review)} sx={{ ml: 0.5 }} aria-label="Edit your review">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={review.rating || 0} precision={0.1} size="small" readOnly />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(review.created_at).toLocaleDateString(language)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {review.title && (
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {translatedProductReviews[review.id]?.title || review.title}
                  </Typography>
                )}
                <Typography variant="body1" sx={{ mb: 2, color: review.comment ? 'text.primary' : 'text.secondary', fontStyle: review.comment ? 'normal' : 'italic' }}>
                  {translatedProductReviews[review.id]?.comment || review.comment || 'No comment provided.'}
                </Typography>

                {/* Category badges */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
                  {[
                    { k: 'price_rating', label: 'Price' },
                    { k: 'accuracy_rating', label: 'As advertised' },
                    { k: 'delivery_rating', label: 'Delivery' },
                    { k: 'packaging_rating', label: 'Packaging' },
                    { k: 'communication_rating', label: 'Communication' },
                    { k: 'product_quality_rating', label: 'Quality' },
                  ].map(({ k, label }) =>
                    review[k] ? (
                      <Chip key={k} label={`${label}: ${review[k]}★`} size="small" color="default" sx={{ bgcolor: 'grey.100' }} />
                    ) : null
                  )}
                </Box>

                {Array.isArray(review.images) && review.images.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    {review.images.slice(0, 4).map((image: string, index: number) => (
                      <Box key={index} sx={{ width: 80, height: 80, borderRadius: 1, overflow: 'hidden' }}>
                        <Box component="img" src={image} alt={`Review ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          ))
        ) : (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No reviews yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Be the first to review this product!
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );

  {/* Edit Review Modal */}

  // Render job-specific layout
  if (isJob) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Back Button */}
          <Button
            startIcon={<BackIcon />}
            onClick={() => {
              if (location.state?.fromMessages) {
                navigate('/all-jobs');
                return;
              }
              if (location.state?.fromAllProducts) {
                navigate('/all-jobs', { 
                  state: { 
                    preserveFilters: true,
                    page: location.state.page,
                    filters: location.state.filters
                  }
                });
              } else {
                navigate(-1);
              }
            }}
            sx={{ mb: 3 }}
          >
            {t('productDetail.buttons.back')}
          </Button>

          {/* Job Header */}
          <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
            <Box sx={{ 
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
              p: 4
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h3" fontWeight="bold" gutterBottom>
                    {productDetail.title}
                  </Typography>
                  {productDetail.company && (
                    <Typography variant="h5" sx={{ opacity: 0.9, mb: 2 }}>
                      {productDetail.company.name}
                    </Typography>
                  )}
                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                         {productDetail.jobType && (
                           <Chip
                             label={productDetail.jobType.replace('-', ' ').toUpperCase()}
                             sx={{
                               bgcolor: 'rgba(255,255,255,0.2)',
                               color: 'white',
                               fontWeight: 'bold',
                               fontSize: '0.8rem'
                             }}
                           />
                         )}
                         {productDetail.experienceLevel && (
                           <Chip
                             label={productDetail.experienceLevel.replace('-', ' ').toUpperCase()}
                             sx={{
                               bgcolor: 'rgba(255,255,255,0.2)',
                               color: 'white',
                               fontWeight: 'bold',
                               fontSize: '0.8rem'
                             }}
                           />
                         )}
                         {productDetail.remoteWorkAllowed && (
                           <Chip
                             label="REMOTE"
                             sx={{
                               bgcolor: 'rgba(255,255,255,0.2)',
                               color: 'white',
                               fontWeight: 'bold',
                               fontSize: '0.8rem'
                             }}
                           />
                         )}
                         {/* Category and Subcategory Chips */}
                         {productDetail.category && (
                           <Chip
                             label={productDetail.category.toUpperCase()}
                             sx={{
                               bgcolor: 'rgba(255,255,255,0.15)',
                               color: 'white',
                               fontWeight: 'bold',
                               fontSize: '0.75rem',
                               border: '1px solid rgba(255,255,255,0.3)'
                             }}
                           />
                         )}
                         {productDetail.subcategory && (
                           <Chip
                             label={productDetail.subcategory.toUpperCase()}
                             sx={{
                               bgcolor: 'rgba(255,255,255,0.1)',
                               color: 'white',
                               fontWeight: 'bold',
                               fontSize: '0.7rem',
                               border: '1px solid rgba(255,255,255,0.2)'
                             }}
                           />
                         )}
                       </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  {productDetail.price > 0 ? (
                    <>
                      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                        ${productDetail.price.toFixed(2)}/hr
                      </Typography>
                      {productDetail.salaryType && (
                        <Typography variant="body1" sx={{ opacity: 0.8 }}>
                          {productDetail.salaryType.charAt(0).toUpperCase() + productDetail.salaryType.slice(1)} Rate
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, opacity: 0.95 }}>
                      Salary discussed in interview
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Job Quick Info - Enhanced Labels */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 2 }}>
                {productDetail.location && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    px: 3,
                    py: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-2px)',
                    }
                  }}>
                    <LocationIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                    <Typography variant="h6" sx={{ 
                      color: '#ffffff', 
                      fontWeight: 600,
                      fontSize: '1.1rem'
                    }}>
                      {productDetail.location}
                    </Typography>
                  </Box>
                )}
                {productDetail.duration && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    px: 3,
                    py: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-2px)',
                    }
                  }}>
                    <ScheduleIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                    <Typography variant="h6" sx={{ 
                      color: '#ffffff', 
                      fontWeight: 600,
                      fontSize: '1.1rem'
                    }}>
                      {productDetail.duration}
                    </Typography>
                  </Box>
                )}
                {productDetail.applicationDeadline && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    px: 3,
                    py: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-2px)',
                    }
                  }}>
                    <EventIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                    <Typography variant="h6" sx={{ 
                      color: '#ffffff', 
                      fontWeight: 700,
                      fontSize: '1.1rem'
                    }}>
                      Apply by {new Date(productDetail.applicationDeadline).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
                {productDetail.expectedStartDate && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    px: 3,
                    py: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-2px)',
                    }
                  }}>
                    <CheckIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                    <Typography variant="h6" sx={{ 
                      color: '#ffffff', 
                      fontWeight: 700,
                      fontSize: '1.1rem'
                    }}>
                      Start {new Date(productDetail.expectedStartDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Job Stats */}
            <Box sx={{ p: 3, bgcolor: 'white' }}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {productDetail.stats?.viewCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Views
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color="info.main">
                    {productDetail.stats?.favoriteCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Saved
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {productDetail.stats?.soldCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Applications
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>

          {/* Company Information */}
          {/* Always show company section for jobs, with fallback if no company data */}
          {(productDetail.company || isJob) && (
            <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                p: 4
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                  {productDetail.company?.logoUrl && (
                    <Box
                      component="img"
                      src={productDetail.company.logoUrl}
                      alt={`${productDetail.company?.name || 'Company'} logo`}
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        objectFit: 'contain',
                        bgcolor: 'white',
                        p: 1,
                        boxShadow: 2
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      {productDetail.company?.name || 'Company Information'}
                    </Typography>
                    {productDetail.company?.industry && (
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {productDetail.company.industry}
                      </Typography>
                    )}
                    {/* Job Category in Company Section */}
                    {(productDetail.category || productDetail.subcategory) && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {productDetail.category && (
                          <Chip
                            label={productDetail.category}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(25, 118, 210, 0.1)',
                              color: 'primary.main',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                        {productDetail.subcategory && (
                          <Chip
                            label={productDetail.subcategory}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(156, 39, 176, 0.1)',
                              color: 'secondary.main',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>
                    )}
                    {productDetail.company?.description ? (
                      <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 600 }}>
                        {productDetail.company.description}
                      </Typography>
                    ) : (
                      <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 600 }}>
                        Learn more about the company offering this position.
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    {productDetail.company?.id ? (
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate(`/companies/${productDetail.company?.slug || productDetail.company?.id}`)}
                        sx={{
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                          }
                        }}
                      >
                        View Company
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        size="large"
                        disabled
                        sx={{
                          borderColor: 'grey.300',
                          color: 'grey.500'
                        }}
                      >
                        Company Info Unavailable
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Company Quick Stats */}
                {productDetail.company && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 3 }}>
                    {productDetail.company.foundedYear && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="body1" fontWeight="bold">
                          Founded {productDetail.company.foundedYear}
                        </Typography>
                      </Box>
                    )}
                    {productDetail.company.employeeCount && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                        <Typography variant="body1" fontWeight="bold">
                          {productDetail.company.employeeCount} employees
                        </Typography>
                      </Box>
                    )}
                    {productDetail.company.headquartersCity && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon sx={{ fontSize: 20, color: 'info.main' }} />
                        <Typography variant="body1" fontWeight="bold">
                          {productDetail.company.headquartersCity}
                          {productDetail.company.headquartersState && `, ${productDetail.company.headquartersState}`}
                        </Typography>
                      </Box>
                    )}
                    {productDetail.company.isRemoteFriendly && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PublicIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                        <Typography variant="body1" fontWeight="bold">
                          Remote Friendly
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Company Values/Benefits */}
                {productDetail.company && ((productDetail.company.values && productDetail.company.values.length > 0) || 
                 (productDetail.company.benefits && productDetail.company.benefits.length > 0)) ? (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Company Culture
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {productDetail.company.values?.slice(0, 4).map((value: string, index: number) => (
                        <Chip
                          key={`value-${index}`}
                          label={value}
                          sx={{
                            bgcolor: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 'bold',
                            fontSize: '0.8rem'
                          }}
                        />
                      ))}
                      {productDetail.company.benefits?.slice(0, 3).map((benefit: string, index: number) => (
                        <Chip
                          key={`benefit-${index}`}
                          label={benefit}
                          sx={{
                            bgcolor: '#e8f5e8',
                            color: '#2e7d32',
                            fontWeight: 'bold',
                            fontSize: '0.8rem'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Card>
          )}

          {/* Main Content */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4 }}>
                 {/* Left Column - Job Details */}
                 <Box sx={{ flex: { xs: '1', lg: '2' } }}>
                   {/* Job Category & Classification */}
                   {(productDetail.category || productDetail.subcategory) && (
                     <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
                       <CardContent sx={{ p: 4 }}>
                         <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                           Job Classification
                         </Typography>
                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                           {productDetail.category && (
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                               <Box
                                 sx={{
                                   width: 8,
                                   height: 8,
                                   borderRadius: '50%',
                                   bgcolor: 'primary.main',
                                   flexShrink: 0
                                 }}
                               />
                               <Typography variant="body1" fontWeight="bold" color="primary.main">
                                 Category:
                               </Typography>
                               <Chip
                                 label={productDetail.category}
                                 sx={{
                                   bgcolor: 'primary.main',
                                   color: 'white',
                                   fontWeight: 'bold',
                                   fontSize: '0.9rem'
                                 }}
                               />
                             </Box>
                           )}
                           {productDetail.subcategory && (
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                               <Box
                                 sx={{
                                   width: 8,
                                   height: 8,
                                   borderRadius: '50%',
                                   bgcolor: 'secondary.main',
                                   flexShrink: 0
                                 }}
                               />
                               <Typography variant="body1" fontWeight="bold" color="secondary.main">
                                 Subcategory:
                               </Typography>
                               <Chip
                                 label={productDetail.subcategory}
                                 sx={{
                                   bgcolor: 'secondary.main',
                                   color: 'white',
                                   fontWeight: 'bold',
                                   fontSize: '0.9rem'
                                 }}
                               />
                             </Box>
                           )}
                         </Box>
                       </CardContent>
                     </Card>
                   )}

                   {/* Job Description */}
                   <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    Job Description
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                    {productDetail.description}
                  </Typography>
                </CardContent>
              </Card>

              {/* Requirements */}
              {productDetail.jobRequirements && productDetail.jobRequirements.length > 0 && (
                <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                      Requirements
                    </Typography>
                    <List>
                      {productDetail.jobRequirements.map((requirement, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <CheckIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={requirement}
                            primaryTypographyProps={{ sx: { lineHeight: 1.6 } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Required Skills */}
              {productDetail.requiredSkills && productDetail.requiredSkills.length > 0 && (
                <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                      Required Skills
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {productDetail.requiredSkills.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          variant="outlined"
                          sx={{
                            fontWeight: 'bold',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Benefits & Perks */}
              {productDetail.benefitsPerks && productDetail.benefitsPerks.length > 0 && (
                <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                      Benefits & Perks
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {productDetail.benefitsPerks.map((benefit, index) => (
                        <Chip
                          key={index}
                          label={benefit}
                          sx={{
                            bgcolor: '#e8f5e8',
                            color: '#2e7d32',
                            fontWeight: 'bold',
                            fontSize: '0.9rem'
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>

            {/* Right Column - Apply Section */}
            <Box sx={{ flex: { xs: '1', lg: '1' } }}>
              <Card sx={{ position: 'sticky', top: 20, borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    Apply for this position
                  </Typography>
                  
                  {/* Contact Information */}
                  {productDetail.contactEmail && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Contact Email:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {productDetail.contactEmail}
                      </Typography>
                    </Box>
                  )}

                  {/* Application Deadline */}
                  {productDetail.applicationDeadline && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Application Deadline:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="warning.main">
                        {new Date(productDetail.applicationDeadline).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}

                  {/* Beautiful Apply Button */}
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => setApplicationModalOpen(true)}
                    sx={{
                      mb: 2,
                      py: 2,
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      color: 'white',
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      boxShadow: '0 8px 32px rgba(25, 118, 210, 0.4)',
                      cursor: 'pointer',
                      zIndex: 9999,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                        boxShadow: '0 12px 40px rgba(25, 118, 210, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        transition: 'left 0.5s',
                        pointerEvents: 'none',
                      },
                      '&:hover::before': {
                        left: '100%',
                      },
                    }}
                  >
                    Apply Now
                  </Button>

                  {/* Save Job Button */}
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    startIcon={<FavoriteIcon />}
                    onClick={handleToggleFavorite}
                    disabled={favoritesLoading}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'white'
                      }
                    }}
                  >
                    {favoritesLoading ? 'Saving...' : (isFavorite ? 'Saved' : 'Save Job')}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Container>
        
        {/* Job Application Modal - INSIDE JOB SECTION */}
        <JobApplicationModal
          open={applicationModalOpen}
          onClose={() => {
            console.log('Closing application modal');
            setApplicationModalOpen(false);
          }}
          jobTitle={productDetail?.title || ''}
          companyName={typeof productDetail?.company === 'string' ? productDetail.company : productDetail?.company?.name || ''}
          productId={productDetail?.id || ''}
          sellerId={productDetail?.sellerId || ''}
          onApply={handleApplicationSubmit}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
          {/* Back Button */}
          <Button
            startIcon={<BackIcon />}
            onClick={() => {
              // Check if we came from the all products page
              if (location.state?.fromMessages) {
                navigate('/all-products');
                return;
              }
              if (location.state?.fromAllProducts) {
                navigate('/all-products', {
                  state: {
                    preserveFilters: true,
                    page: location.state.page,
                    filters: location.state.filters
                  }
                });
              } else {
                navigate(-1);
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('productDetail.buttons.back')}
          </Button>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            {isAuthenticated ? (
              <IconButton
                size="medium"
                color={isFavorite ? 'error' : 'default'}
                onClick={handleToggleFavorite}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 999,
                  bgcolor: 'background.paper',
                }}
                aria-label="Favorite"
              >
                <FavoriteIcon />
              </IconButton>
            ) : (
              <IconButton
                size="medium"
                onClick={() => setAuthRequiredOpen(true)}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 999,
                  bgcolor: 'background.paper',
                }}
                aria-label="Sign in to favorite"
              >
                <LockIcon />
              </IconButton>
            )}

            <IconButton
              size="medium"
              onClick={() => setShareDialogOpen(true)}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 999,
                bgcolor: 'background.paper',
              }}
              aria-label="Share"
            >
              <ShareIcon />
            </IconButton>

            <IconButton
              size="medium"
              onClick={handleOpenReport}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 999,
                bgcolor: 'background.paper',
              }}
              aria-label="Report product"
            >
              <WarningIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Main Product Section */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
          <Box sx={{ flex: { xs: '1', md: '1' }, display: { xs: 'none', md: 'block' } }}>
            {renderImageGallery()}
          </Box>
          <Box sx={{ flex: { xs: '1', md: '1' } }}>
            {renderProductInfo()}
          </Box>
        </Box>

        {/* Seller Info */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 4 }}>
          <Box sx={{ flex: { xs: '1', md: '0 0 33.333%' } }}>
            {renderSellerInfo()}
          </Box>
          <Box sx={{ flex: { xs: '1', md: '0 0 66.666%' } }}>
            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {isJob ? 'Job Requirements' : t('productDetail.specs.title')}
                </Typography>
                <List dense>
                  {isJob ? (
                    <>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Company"
                          secondary={productDetail.specifications.brand || 'Not specified'}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Category"
                          secondary={`${productDetail.specifications.category}${productDetail.specifications.subcategory ? ` • ${productDetail.specifications.subcategory}` : ''}`}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Experience Level"
                          secondary={productDetail.specifications.condition || 'Not specified'}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Job Type"
                          secondary={productDetail.specifications.material || 'Full-time'}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Location"
                          secondary="Remote / Hybrid"
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Positions Available"
                          secondary={`${productDetail.stats.stock || 1} position`}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={t('productDetail.specs.brand')}
                          secondary={productDetail.specifications.brand}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={t('productDetail.specs.category')}
                          secondary={`${productDetail.specifications.category}${productDetail.specifications.subcategory ? ` • ${productDetail.specifications.subcategory}` : ''}`}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={t('productDetail.specs.condition')}
                          secondary={productDetail.specifications.condition}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={t('productDetail.specs.material')}
                          secondary={productDetail.specifications.material}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      {productDetail.specifications.weight && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText
                            primary={t('productDetail.specs.weight')}
                            secondary={productDetail.specifications.weight}
                            primaryTypographyProps={{ fontWeight: 'bold' }}
                          />
                        </ListItem>
                      )}
                      {productDetail.specifications.dimensions && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText
                            primary={t('productDetail.specs.dimensions')}
                            secondary={productDetail.specifications.dimensions}
                            primaryTypographyProps={{ fontWeight: 'bold' }}
                          />
                        </ListItem>
                      )}
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={t('productDetail.specs.stock')}
                          secondary={t('productDetail.specs.available').replace('{count}', String(productDetail.stats.stock))}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Tabs Section */}
        <Card
          sx={{
            ...sectionCardSx,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: { xs: 1, sm: 2 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 800,
                minHeight: 52,
              },
            }}
          >
            <Tab label={isJob ? t('jobDetail.tabs.description') : t('productDetail.tabs.description')} />
            <Tab label={isJob ? t('jobDetail.tabs.requirements') : t('productDetail.tabs.reviews')} />
            <Tab label={isJob ? t('jobDetail.tabs.benefits') : t('productDetail.tabs.shippingReturns')} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography variant="body1" sx={{ lineHeight: 1.75, color: 'text.secondary' }}>
                  {translatedDetail.description || product.description}
                </Typography>
              </Paper>
            </Box>
            
            <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2.5, sm: 3.5 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="h6" fontWeight={900} gutterBottom sx={{ mb: 1.5 }}>
                  {isJob ? "What You'll Do" : "Features"}
                </Typography>
                <List sx={{ m: 0, p: 0 }}>
                  {isJob ? (
                    <>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Develop and maintain web applications using modern technologies" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Collaborate with cross-functional teams to deliver high-quality solutions" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Participate in code reviews and technical discussions" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Contribute to architectural decisions and best practices" />
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="100% Organic Cotton" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Machine Washable" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Pre-shrunk Fabric" />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary="Sustainable Production" />
                      </ListItem>
                    </>
                  )}
                </List>
              </Paper>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {isJob ? (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                <Typography variant="h6" fontWeight={900} gutterBottom sx={{ mb: 2 }}>
                  Required Skills & Experience
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="3+ years of experience in web development"
                      secondary="Proven track record of building scalable applications"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Proficiency in React, TypeScript, and Node.js"
                      secondary="Strong understanding of modern JavaScript frameworks"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Experience with database design and optimization"
                      secondary="Knowledge of SQL and NoSQL databases"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Strong problem-solving and communication skills"
                      secondary="Ability to work independently and in team environments"
                    />
                  </ListItem>
                </List>
                
                <Typography variant="h6" fontWeight={900} gutterBottom sx={{ mb: 2, mt: 3 }}>
                  Nice to Have
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Experience with cloud platforms (AWS, Azure, GCP)"
                      secondary="DevOps and deployment automation knowledge"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Previous startup or fast-paced environment experience"
                      secondary="Adaptability and quick learning ability"
                    />
                  </ListItem>
                </List>
                </Paper>
              </Box>
            ) : (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  {renderReviews()}
                </Paper>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {isJob ? (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Compensation & Benefits
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Competitive Salary"
                        secondary="Based on experience and market rates"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Health Insurance"
                        secondary="Comprehensive medical, dental, and vision coverage"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Flexible Work Arrangements"
                        secondary="Remote work options and flexible hours"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Professional Development"
                        secondary="Learning budget and conference attendance"
                      />
                    </ListItem>
                  </List>
                </Box>

                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Additional Perks
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <StarIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Equity Options"
                        secondary="Stock options and profit sharing"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <StarIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Unlimited PTO"
                        secondary="Take time off when you need it"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <StarIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Modern Equipment"
                        secondary="Latest hardware and software tools"
                      />
                    </ListItem>
                  </List>
                </Box>
              </Box>
                </Paper>
              </Box>
            ) : (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={900} gutterBottom>
                        Shipping Information
                      </Typography>
                      <List>
                        <ListItem>
                          <ListItemIcon>
                            <ShippingIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Free Shipping"
                            secondary="On orders over $50"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <InfoIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Estimated Delivery"
                            secondary="2-3 business days"
                          />
                        </ListItem>
                      </List>
                    </Box>

                    <Box>
                      <Typography variant="h6" fontWeight={900} gutterBottom>
                        Return Policy
                      </Typography>
                      <List>
                        <ListItem>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="30-Day Return Policy"
                            secondary="Return for any reason within 30 days"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <SecurityIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Quality Guarantee"
                            secondary="1-year quality guarantee"
                          />
                        </ListItem>
                      </List>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}
          </TabPanel>
        </Card>
      </Container>

      {/* Edit Review Modal */}
      <ReviewModal
        open={reviewEditOpen}
        productName={productDetail?.title}
        productImage={productImages[0]}
        price={productDetail?.price}
        date={reviewTarget?.created_at}
        productId={productDetail?.id}
        loading={reviewSubmitting}
        rating={reviewRating}
        title={reviewTitle}
        comment={reviewComment}
        onChangeRating={setReviewRating}
        onChangeTitle={setReviewTitle}
        onChangeComment={setReviewComment}
        userShowAlias={reviewUserShowAlias}
        onChangeUserShowAlias={setReviewUserShowAlias}
        priceRating={reviewPriceRating}
        accuracyRating={reviewAccuracyRating}
        deliveryRating={reviewDeliveryRating}
        packagingRating={reviewPackagingRating}
        communicationRating={reviewCommunicationRating}
        qualityRating={reviewQualityRating}
        onChangePriceRating={setReviewPriceRating}
        onChangeAccuracyRating={setReviewAccuracyRating}
        onChangeDeliveryRating={setReviewDeliveryRating}
        onChangePackagingRating={setReviewPackagingRating}
        onChangeCommunicationRating={setReviewCommunicationRating}
        onChangeQualityRating={setReviewQualityRating}
        onClose={() => setReviewEditOpen(false)}
        onSubmit={submitReviewUpdate}
      />

      {/* Sign In Prompt Modal */}
      <SignInPromptModal
        open={signInModalOpen}
        onClose={() => setSignInModalOpen(false)}
        title="Sign In Required"
        message="You need to sign in to save products to your favorites."
      />

      {/* Bid Placement Popup (same one used by AuctionCard) */}
      {productDetail?.auctionId && productDetail?.auction && (
        <BidPlacementPopup
          open={bidModalOpen}
          onClose={() => setBidModalOpen(false)}
          product={{
            id: productDetail.id,
            name: productDetail.title,
            image: productDetail.images?.[0] || '/placeholder-product.jpg',
            price: productDetail.price,
            auction: {
              id: productDetail.auction.id,
              end_time: productDetail.auction.end_time,
              current_bid: productDetail.auction.current_bid,
              bid_increment: productDetail.auction.bid_increment,
              status: productDetail.auction.status,
              reserve_price: productDetail.auction.reserve_price,
              bid_count: productDetail.auction.bid_count,
              highest_bidder: productDetail.auction.highest_bidder,
            },
          }}
          onPlaceBid={async (bidAmount: number) => {
                await AuctionService.createBid({ auction_id: productDetail.auction!.id, bid_amount: bidAmount });
          }}
        />
      )}

      {/* Bid Placement History Popup */}
      {productDetail?.auctionId && productDetail?.auction && (
        <BidPlacementHistoryPopup
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          auctionId={productDetail.auction.id}
          productName={productDetail.title}
        />
      )}

      {/* Photo Gallery Popup */}
      <PhotoGalleryPopup
        open={photoGalleryOpen}
        onClose={() => setPhotoGalleryOpen(false)}
        images={productImages}
        currentIndex={selectedImage}
        onIndexChange={setSelectedImage}
        productTitle={product?.title || ''}
      />

      {/* Auth Required Popup */}
      <AuthRequiredPopup
        open={authRequiredOpen}
        onClose={() => setAuthRequiredOpen(false)}
        onSignIn={() => {
          setAuthRequiredOpen(false);
          setSignInModalOpen(true);
        }}
        onSignUp={() => {
          setAuthRequiredOpen(false);
          navigate('/signup');
        }}
        feature="all product photos"
        description="Sign in to view the complete photo gallery and explore all product images in fullscreen mode!"
      />

      {/* New Offer Popup */}
      {productDetail && isOfferProduct && (
        <NewOfferPopup
          open={offerPopupOpen}
          onClose={() => setOfferPopupOpen(false)}
          product={{
            id: productDetail.id,
            name: productDetail.title,
            price: productDetail.price,
            image: productDetail.images?.[0] || '/placeholder-product.jpg',
            sellerId: productDetail.sellerId,
            seller: {
              name: productDetail.seller.name,
              avatar: productDetail.seller.avatar,
            },
          }}
          onSuccess={() => {
            setOfferPopupOpen(false);
            // Optionally show a success notification or refresh data
            console.log('Offer created successfully');
          }}
        />
      )}

      <ReportProductDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        onSubmit={handleSubmitReport}
        productTitle={productDetail?.title}
        sellerName={productDetail?.seller?.name}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        id={productDetail?.id}
        url={typeof window !== 'undefined' ? window.location.href : undefined}
        title={productDetail?.title}
      />

    </Box>
  );
};

export default ProductDetailPage;
